/**
 * Native authentication for the Capacitor iOS / iPadOS / Mac build.
 * On the web, every function in this module is a no-op — cookie auth
 * handles it there.
 *
 * Flow overview (PKCE, RFC 7636):
 *
 *   1. `startNativeLogin()` generates a random `code_verifier` and
 *      derives `code_challenge = base64url(SHA256(verifier))`. The
 *      verifier goes to Keychain (Capacitor Preferences), the
 *      challenge goes on the wire.
 *
 *   2. We open WorkOS's hosted AuthKit page via our custom
 *      `AuthSession` Capacitor plugin (wraps `ASWebAuthenticationSession`).
 *      That plugin is the only iOS primitive Apple blesses for OAuth
 *      callbacks to custom URL schemes — SFSafariViewController
 *      silently refuses to hand off custom schemes since iOS 15, which
 *      is what bit the previous `@capacitor/browser` implementation.
 *
 *   3. When WorkOS redirects to `busswerks://auth?code=…`, iOS auto-
 *      closes the auth sheet and returns the full callback URL to our
 *      plugin. We extract the short-lived authorization code.
 *
 *   4. We POST the code + saved verifier to WorkOS's token endpoint
 *      directly. No server round-trip — because PKCE replaces the
 *      client_secret with the verifier, public clients can do the
 *      token exchange themselves. WorkOS returns `access_token` +
 *      `refresh_token` + user object.
 *
 *   5. Tokens land in Keychain via Capacitor Preferences; React Query
 *      refetches `/api/v1/users/me` once we navigate to `/home`.
 *
 * On cold launch, `initNativeAuth()` restores the tokens from Keychain
 * so the first `useAuth()` call succeeds without a round-trip through
 * the sign-in screen.
 */

import { registerPlugin } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Preferences } from '@capacitor/preferences'
import {
  clearAuthTokens,
  getAuthToken,
  onNativeTokensRefreshed,
  setAuthTokens,
} from '@/lib/api'
import { isNativePlatform } from '@/lib/nativePlatform'
import { deriveCodeChallenge, generateCodeVerifier } from '@/lib/pkce'

const ACCESS_KEY = 'ac.auth.access'
const REFRESH_KEY = 'ac.auth.refresh'
const PKCE_VERIFIER_KEY = 'ac.auth.pkce_verifier'

// WorkOS's hosted authorize page. The AuthSession plugin loads this
// URL in an ASWebAuthenticationSession; WorkOS redirects back to our
// custom scheme with a short-lived authorization code.
//
// `VITE_WORKOS_AUTH_DOMAIN`, when set, overrides the default to point
// at a custom AuthKit domain (e.g. `auth.busswerks.io`). The flow is
// otherwise identical — still PKCE, still hosted AuthKit — just on
// our domain. Keeping the fallback means environments that haven't
// been migrated to a custom domain keep working.
const WORKOS_AUTH_DOMAIN =
  (import.meta.env.VITE_WORKOS_AUTH_DOMAIN as string | undefined) ||
  'api.workos.com'
const WORKOS_AUTHORIZE_BASE = `https://${WORKOS_AUTH_DOMAIN}`

// Our API base URL — baked in at build time. The token exchange
// (code + verifier → access/refresh tokens) is proxied through our
// backend's `/api/v1/auth/pkce` endpoint rather than hitting
// `api.workos.com/user_management/authenticate` directly, because
// WorkOS doesn't send CORS headers for the `capacitor://localhost`
// origin the WebView runs from. The server then calls WorkOS with
// the matching client_id from Secrets Manager. Security properties
// of PKCE are preserved — the code_verifier is still client-owned
// and never leaves the device except in that single proxied POST.
const API_BASE = (import.meta.env.VITE_API_URL ?? '') as string

// The WorkOS public client id. Baked at build time via
// `VITE_WORKOS_CLIENT_ID`. `client_id` is a public identifier — unlike
// `WORKOS_API_KEY` (server-side Secrets Manager) — so bundling it into
// the SPA is explicitly supported by WorkOS.
const WORKOS_CLIENT_ID = (import.meta.env.VITE_WORKOS_CLIENT_ID ?? '') as string

// Custom URL scheme registered in `Info.plist → CFBundleURLSchemes`.
// ASWebAuthenticationSession watches for URLs with this scheme and
// auto-closes the sheet when one appears, delivering the callback URL
// to our plugin.
const NATIVE_CALLBACK_URL = 'busswerks://auth'
const NATIVE_CALLBACK_SCHEME = 'busswerks'

// Typed surface for our Swift plugin (see
// packages/mobile/ios/App/App/AuthSession.swift). `open()` resolves with
// the full callback URL the server redirected to, or rejects with
// `'USER_CANCELLED'` on user cancel.
interface AuthSessionPlugin {
  open(options: {
    url: string
    callbackScheme: string
  }): Promise<{ url: string }>
}

const AuthSession = registerPlugin<AuthSessionPlugin>('AuthSession')

let listenersRegistered = false

// @capacitor/preferences and @capacitor/app are imported STATICALLY
// at the top of this file, not via dynamic `import(...)`. Reason:
// Capacitor's WKWebView serves the app from `capacitor://localhost`
// (or `https://localhost` on iOS proper when `ios.scheme:'https'`
// applies — which it doesn't on Mac Catalyst and some simulator
// configurations). Dynamic import fetches go through the custom
// URL scheme handler, which can hang indefinitely on Mac Catalyst
// and iPad Simulator for small split chunks. The static imports
// land in the main entry chunk, which is loaded eagerly at page
// load, bypassing the problem entirely. Trade: slightly larger
// initial bundle (~few KB) for guaranteed availability at runtime.

async function readStoredTokens(): Promise<{
  access: string | null
  refresh: string | null
}> {
  const [access, refresh] = await Promise.all([
    Preferences.get({ key: ACCESS_KEY }),
    Preferences.get({ key: REFRESH_KEY }),
  ])
  return {
    access: access.value || null,
    refresh: refresh.value || null,
  }
}

async function writeStoredTokens(access: string, refresh: string) {
  await Promise.all([
    Preferences.set({ key: ACCESS_KEY, value: access }),
    Preferences.set({ key: REFRESH_KEY, value: refresh }),
  ])
}

async function clearStoredTokens() {
  await Promise.all([
    Preferences.remove({ key: ACCESS_KEY }),
    Preferences.remove({ key: REFRESH_KEY }),
    Preferences.remove({ key: PKCE_VERIFIER_KEY }),
  ])
}

/**
 * Legacy deep-link handler. The new PKCE flow delivers the callback
 * URL through the AuthSession plugin's resolve, not through iOS's
 * generic URL-open pipeline — so this listener is no longer the
 * primary sign-in path. We keep it for two reasons:
 *
 *   1. Already-installed TestFlight builds from the previous (pre-PKCE)
 *      flow will still route through the server's
 *      `busswerks://auth?access=&refresh=` redirect. Parsing those URLs
 *      keeps those users from hanging.
 *
 *   2. Future deep links (share-a-layout, invite emails) will reuse
 *      the `busswerks://` scheme with other paths, and we'll want an
 *      `appUrlOpen` listener then.
 */
async function handleLegacyAuthUrl(url: string): Promise<boolean> {
  try {
    // URLSearchParams doesn't parse custom schemes; rewrite to https
    // first so the parser works.
    const parsed = new URL(url.replace(/^busswerks:\/\//, 'https://'))
    const access = parsed.searchParams.get('access')
    const refresh = parsed.searchParams.get('refresh')
    if (!access || !refresh) return false
    setAuthTokens(access, refresh)
    await writeStoredTokens(access, refresh)
    return true
  } catch {
    return false
  }
}

/**
 * Initialize native auth: restore tokens from Keychain, register the
 * (legacy) URL-open listener, and wire up the apiClient → Keychain
 * rotation hook. Safe to call on web (no-op).
 */
export async function initNativeAuth(): Promise<void> {
  if (!isNativePlatform()) return
  if (listenersRegistered) return
  listenersRegistered = true

  try {
    // 1. Restore tokens so the first /auth/me call succeeds without
    //    forcing the user through the Login screen on every cold start.
    const stored = await readStoredTokens()
    if (stored.access && stored.refresh) {
      setAuthTokens(stored.access, stored.refresh)
    }

    // 2. Legacy deep-link listener — kept as a safety net for old
    //    TestFlight builds that still get server-minted tokens in the
    //    URL. The new PKCE flow does NOT route through here.
    App.addListener('appUrlOpen', async event => {
      if (!event.url.startsWith('busswerks://')) return
      const ok = await handleLegacyAuthUrl(event.url)
      if (ok) {
        window.location.href = '/home'
      }
    })

    // 3. When the apiClient rotates tokens via silent refresh, persist
    //    the new values so the next cold launch picks them up.
    onNativeTokensRefreshed((access, refresh) => {
      void writeStoredTokens(access, refresh)
    })
  } catch (err) {
    console.warn('[nativeAuth] init failed:', err)
  }
}

/**
 * Kick off the WorkOS sign-in flow. On web this navigates to the
 * server's cookie-based OAuth endpoint. On native it runs the full
 * PKCE flow end-to-end.
 */
export async function startNativeLogin(): Promise<void> {
  console.log('[nativeAuth] startNativeLogin begin')
  if (!isNativePlatform()) {
    // Same cross-origin fix as `routes/login.tsx` — prefix with
    // API_BASE so localhost → dev API works. No-op in production
    // where API_BASE is empty.
    const origin = encodeURIComponent(window.location.origin)
    window.location.href = `${API_BASE}/api/v1/auth/login?origin=${origin}`
    return
  }

  if (!WORKOS_CLIENT_ID) {
    throw new Error(
      'VITE_WORKOS_CLIENT_ID is not set — cannot start native login. ' +
        'Add it as a build-time env var (see packages/app/.env.example).',
    )
  }

  // 1. PKCE: fresh verifier on every sign-in attempt. Persist the
  //    verifier to Keychain BEFORE opening the auth sheet so that if the
  //    app is backgrounded mid-flow and resumed, we can still exchange
  //    the code.
  console.log('[nativeAuth] generating PKCE verifier')
  const verifier = generateCodeVerifier()
  console.log(
    '[nativeAuth] verifier len=',
    verifier.length,
    '— deriving challenge',
  )
  const challenge = await deriveCodeChallenge(verifier)
  console.log('[nativeAuth] challenge derived, stashing verifier')
  await Preferences.set({ key: PKCE_VERIFIER_KEY, value: verifier })
  console.log('[nativeAuth] PKCE verifier stashed, opening AuthSession')

  // 2. Build the authorize URL. `provider=authkit` asks WorkOS to show
  //    the hosted AuthKit picker (email/SSO/Apple/etc.) rather than
  //    routing straight to one provider. Once Sign in with Apple is
  //    enabled as a Connection in the WorkOS dashboard, it appears as
  //    a button on that hosted page with no additional client code.
  const authorizeUrl =
    `${WORKOS_AUTHORIZE_BASE}/user_management/authorize?` +
    new URLSearchParams({
      client_id: WORKOS_CLIENT_ID,
      redirect_uri: NATIVE_CALLBACK_URL,
      response_type: 'code',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      provider: 'authkit',
    }).toString()

  // 3. Present the system auth sheet. Resolves with the callback URL
  //    iOS intercepted (e.g. `busswerks://auth?code=abc123`).
  let callbackUrl: string
  try {
    console.log('[nativeAuth] AuthSession.open() call ->', authorizeUrl)
    const result = await AuthSession.open({
      url: authorizeUrl,
      callbackScheme: NATIVE_CALLBACK_SCHEME,
    })
    console.log('[nativeAuth] AuthSession.open() resolved', result.url)
    callbackUrl = result.url
  } catch (err) {
    // User-cancel shouldn't be treated as an error upstream.
    const msg =
      err instanceof Error ? err.message : String(err ?? 'unknown error')
    if (msg === 'USER_CANCELLED' || /cancel/i.test(msg)) return
    console.error('[nativeAuth] ASWebAuthenticationSession failed:', err)
    throw err
  }

  // 4. Extract the authorization code. Rewrite the custom scheme so
  //    URL() parses cleanly; the host doesn't matter since we only
  //    read searchParams.
  const parsed = new URL(callbackUrl.replace(/^busswerks:\/\//, 'https://'))
  const code = parsed.searchParams.get('code')
  const errorParam = parsed.searchParams.get('error')
  if (errorParam) {
    throw new Error(`WorkOS returned error: ${errorParam}`)
  }
  if (!code) {
    throw new Error('WorkOS callback missing authorization code')
  }

  // 5. Exchange code + verifier for tokens via our backend proxy.
  //    WorkOS's `/user_management/authenticate` doesn't send CORS
  //    headers for the `capacitor://localhost` origin the WebView
  //    runs from, so a direct client → WorkOS POST fails with a
  //    generic "Load failed". Our backend sits in the CORS allowlist
  //    and holds the WorkOS client id in Secrets Manager, so it
  //    can execute the exchange on the client's behalf while we
  //    still keep the PKCE verifier on the device.
  if (!API_BASE) {
    throw new Error(
      'VITE_API_URL is not set — cannot reach PKCE exchange endpoint. ' +
        'Set VITE_API_URL in packages/app/.env.local (e.g. https://dev.busswerks.io) and rebuild.',
    )
  }
  const pkceUrl = `${API_BASE}/api/v1/auth/pkce`
  console.log('[nativeAuth] POST', pkceUrl)
  let tokenRes: Response
  try {
    tokenRes = await fetch(pkceUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, codeVerifier: verifier }),
    })
  } catch (err) {
    // WebKit throws `TypeError: Load failed` on any network-layer
    // failure — CORS, DNS, offline, wrong origin. The error object
    // serializes to `{}` via console.error so we rebuild a readable
    // message with the actual URL.
    const msg = err instanceof Error ? err.message : String(err)
    throw new Error(
      `Network error POST ${pkceUrl} — ${msg}. ` +
        'Check: (a) VITE_API_URL points at a deployed backend, ' +
        '(b) that backend has /api/v1/auth/pkce deployed, ' +
        '(c) API Gateway CORS allows capacitor://localhost.',
    )
  }
  console.log('[nativeAuth] /api/v1/auth/pkce status', tokenRes.status)

  if (!tokenRes.ok) {
    const body = await tokenRes.text().catch(() => '<no body>')
    throw new Error(`PKCE token exchange failed (${tokenRes.status}): ${body}`)
  }

  // Guard against the CloudFront → SPA fallback. When /api/v1/auth/pkce
  // isn't deployed yet, the dev distribution serves the SPA's index.html
  // as a 200 response instead of returning 404 — so tokenRes.ok is true
  // but the body is HTML, not JSON. Detect and surface a useful error.
  const contentType = tokenRes.headers.get('content-type') ?? ''
  if (!contentType.toLowerCase().includes('json')) {
    const body = await tokenRes.text().catch(() => '<unreadable>')
    const preview = body.slice(0, 200).replace(/\s+/g, ' ')
    throw new Error(
      `PKCE endpoint returned non-JSON (${contentType || 'no content-type'}): "${preview}…". ` +
        "Likely the /api/v1/auth/pkce route isn't deployed — run `npm run deploy:dev` from the repo root.",
    )
  }

  const payload = (await tokenRes.json()) as {
    access?: string
    refresh?: string
    pending?: boolean
    pendingToken?: string
    email?: string
  }

  // PKCE verifier has served its purpose. Clear it before any branch
  // so a subsequent failed attempt doesn't accidentally reuse it.
  await Preferences.remove({ key: PKCE_VERIFIER_KEY })

  // First-time native sign-up: the server withholds tokens until the
  // user redeems an invite code. Stash the pending token in
  // sessionStorage for the invite-gate UI and navigate there.
  if (payload.pending && payload.pendingToken) {
    sessionStorage.setItem('ac.auth.pendingToken', payload.pendingToken)
    if (payload.email) {
      sessionStorage.setItem('ac.auth.pendingEmail', payload.email)
    }
    window.location.href = '/invite-gate'
    return
  }

  if (!payload.access || !payload.refresh) {
    throw new Error('Token response missing access or refresh')
  }

  // 6. Persist + hand off to React Query by navigating to /home. Hard
  //    navigation (window.location) beats router.push because it flushes
  //    every in-memory cache cleanly — simplest way to avoid stale
  //    `useAuth()` data from before sign-in.
  setAuthTokens(payload.access, payload.refresh)
  await writeStoredTokens(payload.access, payload.refresh)

  window.location.href = '/home'
}

/**
 * Clear stored tokens and log the user out on native. Web still uses
 * the `/api/v1/auth/logout` endpoint which clears the cookies.
 */
export async function logoutNative(): Promise<void> {
  if (!isNativePlatform()) return
  clearAuthTokens()
  await clearStoredTokens().catch(() => {})
}

export function hasNativeSession(): boolean {
  return isNativePlatform() && !!getAuthToken()
}
